import { Suspense, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@reflex/ui/components/ui/alert';
import { PageDescription } from '@reflex/ui/components/layout/Typography';
import { IntegrationEmbedProvider } from '@reflex/ui/components/layout/IntegrationEmbed';
import { getPluginComponent } from '@/lib/plugin-registry';
import type { PluginManifest } from '@/lib/plugin-registry';

/**
 * Resolves a plugin's per-org setup component from the plugin registry
 * and renders it inside a `<Suspense>` boundary. Mirrors
 * `PluginAdminSetupPanel` for the org-tier surface — used by both:
 *
 *   - the org-onboarding wizard's `WizardPluginSetupSheet` slide-over
 *   - the plugin detail page (`/orgs/${slug}/configure/connect/plugins/<name>`)
 *     via `PluginDetailContent`
 *
 * Component is rendered with no props — read the active organization
 * via `useAuth().activeOrganizationId` or whatever app-level context
 * the plugin already depends on.
 *
 * Returns `null` when the plugin does not declare an `orgSetupComponent`.
 * Renders an inline error when the component is declared but not
 * registered in the plugin's `web/register.ts` (also surfaced via
 * `collectPluginWebWiringIssues` at boot).
 */
export function PluginOrgSetupPanel({
  plugin,
  hideLabel = false,
}: {
  plugin: PluginManifest;
  /** When true, skip the manifest label (the host already shows it). */
  hideLabel?: boolean;
}) {
  const orgSetup = plugin.web?.orgSetupComponent;
  // Stable lazy reference across renders — same pattern as
  // `PluginAdminSetupPanel`. See that file for the rationale on why this
  // does not trip the `react-hooks/static-components` rule.
  const resolved = useMemo(
    () => ({ component: orgSetup ? getPluginComponent(orgSetup.componentPath) : undefined }),
    [orgSetup],
  );
  const { component: Component } = resolved;

  if (!orgSetup) return null;

  if (!Component) {
    return (
      <Alert variant="destructive" data-testid="plugin-org-setup-panel-missing">
        <AlertTitle>Org setup panel not registered</AlertTitle>
        <AlertDescription>
          The <code className="font-mono">{plugin.name}</code> plugin declared an org setup
          component (<code className="font-mono">{orgSetup.componentPath}</code>) but it is not
          registered in its <code className="font-mono">web/register.ts</code>.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <section data-testid={`plugin-org-setup-panel-${plugin.name}`} className="flex flex-col gap-3">
      {!hideLabel && orgSetup.label && (
        <PageDescription className="text-xs">{orgSetup.label}</PageDescription>
      )}
      <Suspense
        fallback={
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            Loading setup panel…
          </div>
        }
      >
        <IntegrationEmbedProvider>
          <Component />
        </IntegrationEmbedProvider>
      </Suspense>
    </section>
  );
}
